import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, ChevronLeft, Sparkles, Scale, Target, Loader2 } from "lucide-react";
import { getJourney, explore, decide, saveFuture } from "../lib/api";
import { PathwayCard } from "../components/PathwayCard";
import { PhotoPanel } from "../components/PhotoPanel";
import { useFutureSelf } from "../lib/futureSelf";
import { MotivationStrip } from "../components/MotivationStrip";
import { DeepQuestion } from "../components/DeepQuestion";
import { QuantumHub } from "../components/QuantumHub";
import { PathSteps } from "../components/PathSteps";

const OPTIONS = [
  "What if I made $200,000 a year?",
  "What if I started my own business?",
  "What if I got married and had children?",
  "What if I moved abroad?",
  "What if I pursued my dream full-time?",
];

export default function Journey() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const fs = useFutureSelf(id);
  const [question, setQuestion] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [futures, setFutures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const j = await getJourney(id);
        setJourney(j);
      } catch {
        toast.error("Journey not found.");
        navigate("/");
      }
    })();
  }, [id, navigate]);

  const ask = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setFutures([]);
    setCurrentQuestion(q);
    try {
      const data = await explore(id, q);
      setFutures(data.futures || []);
    } catch {
      toast.error("The simulation stuttered. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const choose = async (f) => {
    setDeciding(true);
    try {
      const data = await decide(id, currentQuestion, f);
      const j = await getJourney(id);
      setJourney(j);
      setFutures([]);
      setQuestion("");
      setCurrentQuestion("");
      if (data.mirror_due) {
        navigate(`/mirror/${id}`);
      } else {
        toast.success(`Decision ${data.decisions_count} woven into your path.`, {
          description: f.title,
        });
      }
    } catch {
      toast.error("Couldn't record decision.");
    } finally {
      setDeciding(false);
    }
  };

  const saveAndExplore = async (f) => {
    try {
      await saveFuture(id, { ...f, question: currentQuestion });
      toast.success("Future saved to your compare dashboard.");
    } catch {
      toast.error("Save failed.");
    }
  };

  if (!journey) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        <Loader2 className="animate-spin mr-2" /> Weaving your simulation…
      </div>
    );
  }

  const decisions = journey.decisions || [];
  const latest = decisions[decisions.length - 1];

  return (
    <div className="min-h-screen px-5 md:px-8 py-6 max-w-7xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <button onClick={() => navigate("/")} className="btn-pill text-xs" data-testid="home-btn">
          <ChevronLeft size={14} /> Home
        </button>
        <div className="font-mono-label text-white/50 text-center flex-1">
          Journey · {decisions.length} decision{decisions.length === 1 ? "" : "s"} woven
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(`/compare/${id}`)} className="btn-pill text-xs" data-testid="compare-btn">
            <Scale size={14} /> Compare
          </button>
          <button onClick={() => navigate(`/journal/${id}`)} className="btn-pill text-xs" data-testid="journal-btn">
            <BookOpen size={14} /> Journal
          </button>
        </div>
      </div>

      <MotivationStrip />
      <QuantumHub journey={journey} />

      {/* Scene */}
      <div className="grid md:grid-cols-5 gap-8 mt-8">
        {/* Left: avatar + scene */}
        <div className="md:col-span-2">
          <div className="glass rounded-3xl p-8 relative overflow-hidden">
            <DeepQuestion journeyId={id} fs={fs} />
            <PhotoPanel fs={fs} />
            <motion.p
              key={latest?.id || "origin"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 pt-4 border-t border-white/10 text-sm italic text-amber-200/90 leading-relaxed"
              data-testid="encouragement-text"
            >
              {latest?.encouragement ||
                "What if the life you keep imagining is only a few brave decisions away? Ask your first question and watch it take shape."}
            </motion.p>
          </div>

          <PathSteps journeyId={id} latest={latest} fs={fs} />
        </div>

        {/* Right: question + futures */}
        <div className="md:col-span-3">
          <div className="glass rounded-3xl p-6 md:p-8">
            <div className="font-mono-label text-white/50 mb-3">Ask the rabbit hole</div>
            <div className="flex gap-2 flex-col md:flex-row">
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask(question)}
                placeholder=""
                data-testid="question-input"
                className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-white focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_18px_rgba(0,240,255,0.25)]"
              />
              <button onClick={() => ask(question)} disabled={loading} data-testid="ask-btn" className="btn-pill btn-pill-primary justify-center">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Unfold
              </button>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              {OPTIONS.map((s, i) => (
                <button
                  key={s}
                  onClick={() => setQuestion(s)}
                  data-testid={`option-${i}`}
                  className="text-left text-sm px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.02] text-white/65 hover:border-cyan-400/50 hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass rounded-3xl p-10 text-center text-white/60"
                >
                  <Loader2 className="mx-auto mb-3 animate-spin" />
                  Three futures are forming in the dark…
                </motion.div>
              )}
              {!loading && futures.length > 0 && (
                <motion.div
                  key="futures"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid gap-4 md:grid-cols-3"
                >
                  {futures.map((f, i) => (
                    <div key={i} className="relative">
                      <PathwayCard
                        future={f}
                        index={i}
                        onChoose={choose}
                        testId={`future-card-${i}`}
                      />
                      <div className="mt-2 flex gap-2 justify-between">
                        <button
                          onClick={() => saveAndExplore(f)}
                          className="text-[11px] text-white/40 hover:text-white/80 transition-colors"
                          data-testid={`save-future-${i}`}
                        >
                          Save to compare
                        </button>
                        <button
                          onClick={() => navigate(`/plan/${id}?title=${encodeURIComponent(f.title)}&summary=${encodeURIComponent(f.summary)}`)}
                          className="text-[11px] text-amber-300/80 hover:text-amber-200 flex items-center gap-1"
                          data-testid={`plan-future-${i}`}
                        >
                          <Target size={11} /> Turn into plan
                        </button>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
              {!loading && futures.length === 0 && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12"
                  data-testid="empty-futures"
                >
                  <div className="font-hero text-3xl md:text-5xl leading-tight">Three futures are waiting.</div>
                  <div className="font-mono-label mt-4">Ask above and watch them appear</div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {deciding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto mb-4 animate-spin text-cyan-400" size={32} />
            <div className="font-display text-xl">Stepping through the pathway…</div>
          </div>
        </div>
      )}
    </div>
  );
}
